/**
 * Product Page Layout Control
 * 产品页面布局控制脚本
 * 
 * 功能：
 * 1. 合并了 layout-control.js 和 move-quantity.js 的功能
 * 2. 根据设备类型（桌面端/移动端）应用不同的布局
 * 3. 桌面端（≥768px）：调整 Size Guide 按钮、数量选择器、评价徽章的位置
 * 4. 移动端（<768px）：重新排列产品页面元素的顺序
 *    - 颜色选择器移到产品图片下方
 *    - 尺寸选择器移到材质说明之后
 *    - Complete the look 模块和加购按钮已在最后，无需移动
 * 
 * 更新：仅在产品详情页执行，避免在其他页面产生控制台警告
 */

(function() {
    'use strict';

    // ============================================
    // 配置常量
    // ============================================
    const MOBILE_BREAKPOINT = 768; // 移动端断点（像素）
    const DEBUG_MODE = false; // Debug 模式开关，设为 true 启用详细日志
    let isLayoutApplied = false; // 防止重复执行布局调整

    // ============================================
    // 工具函数
    // ============================================

    /**
     * Debug 日志函数
     * 只在 DEBUG_MODE 为 true 时输出日志
     */
    function debugLog(...args) {
        if (DEBUG_MODE) {
            console.log(...args);
        }
    }

    /**
     * Debug 错误日志函数
     * 始终输出错误，但详细错误信息只在 DEBUG_MODE 为 true 时输出
     */
    function debugError(...args) {
        if (DEBUG_MODE) {
            console.error(...args);
        }
    }

    /**
     * 检测是否为移动端设备
     * @returns {boolean} 如果是移动端返回 true
     */
    function isMobile() {
        return window.innerWidth <= MOBILE_BREAKPOINT;
    }

    /**
     * 防抖函数，限制函数执行频率
     * @param {Function} func - 要防抖的函数
     * @param {number} wait - 等待时间（毫秒）
     * @returns {Function} 防抖后的函数
     */
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * 检查是否为产品详情页
     * @returns {boolean} 如果是产品详情页返回 true
     */
    function isProductPage() {
        return document.body && document.body.classList.contains('template-product');
    }

    // ============================================
    // 桌面端布局逻辑（≥768px）
    // ============================================

    /**
     * 【桌面端】调整 Size 选项布局
     * 操作：隐藏 Size 标签，将 Size Guide 按钮移动到尺寸选择按钮容器内
     * 源元素：.product__label（在 Size 选项容器内）
     * 目标位置：.variant-popup__trigger → 移动到 .product__color-chips 容器内
     * 样式调整：Size Guide 按钮添加 padding-left: 4%
     */
    function adjustSizeOptionLayout() {
        debugLog('🔍 [Debug] 开始调整 Size 选项布局');
        
        // 查找所有选项容器
        debugLog('🔍 [Debug] 查找所有选项容器: [data-index^="option"]');
        const allOptionContainers = document.querySelectorAll('[data-index^="option"]');
        debugLog('  - 找到选项容器数量:', allOptionContainers.length);
        
        if (allOptionContainers.length === 0) {
            debugLog('ℹ️ PDP Layout Control: No option containers found on this product page');
            return;
        }

        // 通过标签文本查找 Size 选项容器
        debugLog('🔍 [Debug] 查找 Size 选项容器（通过标签文本）');
        let sizeOptionContainer = null;
        for (const container of allOptionContainers) {
            const labelElement = container.querySelector('.product__label');
            if (labelElement && labelElement.textContent.trim().toLowerCase() === 'size') {
                sizeOptionContainer = container;
                debugLog('  - ✅ 找到 Size 选项容器');
                break;
            }
        }

        if (!sizeOptionContainer) {
            debugLog('❌ [Debug] Size 选项容器未找到');
            return;
        }

        // 查找目标容器（尺寸选择按钮容器）
        debugLog('🔍 [Debug] 查找尺寸选择按钮容器: .product__color-chips');
        const colorChipsContainer = sizeOptionContainer.querySelector('.product__color-chips');
        debugLog('  - 找到:', colorChipsContainer ? '是' : '否');
        
        if (!colorChipsContainer) {
            debugError('❌ [Debug] 目标容器 .product__color-chips 未找到');
            return;
        }

        // 【操作1】隐藏 Size 标签
        debugLog('🔍 [Debug] 查找 Size 标签: .product__label');
        const sizeLabelElement = sizeOptionContainer.querySelector('.product__label');
        debugLog('  - 找到:', sizeLabelElement ? '是' : '否');
        
        if (sizeLabelElement) {
            const currentDisplay = sizeLabelElement.style.display;
            sizeLabelElement.style.display = 'none';
            // debugLog('✅ Size 标签已隐藏');
            debugLog('  - 原样式 display:', currentDisplay || '未设置');
            debugLog('  - 新样式 display: none');
        } else {
            debugLog('ℹ️ [Debug] Size 标签未找到，可能已经隐藏');
        }

        // 【操作2】移动 Size Guide 按钮到尺寸选择按钮容器内
        debugLog('🔍 [Debug] 查找 Size Guide 按钮: .variant-popup__trigger');
        const sizeGuideButton = sizeOptionContainer.querySelector('.variant-popup__trigger');
        debugLog('  - 找到:', sizeGuideButton ? '是' : '否');
        
        if (sizeGuideButton) {
            const isAlreadyInTarget = colorChipsContainer.contains(sizeGuideButton);
            debugLog('📦 [Debug] 检查 Size Guide 按钮位置');
            debugLog('  - 当前位置:', sizeGuideButton.parentNode ? sizeGuideButton.parentNode.className : '未知');
            debugLog('  - 目标容器:', colorChipsContainer.className);
            debugLog('  - 已在目标容器内:', isAlreadyInTarget ? '是' : '否');
            
            if (!isAlreadyInTarget) {
                debugLog('📦 [Debug] 开始移动 Size Guide 按钮');
                const oldParent = sizeGuideButton.parentNode;
                colorChipsContainer.appendChild(sizeGuideButton);
                // debugLog('✅ Size Guide 按钮已移动到尺寸选择按钮容器内');
                debugLog('  - 原父容器:', oldParent ? oldParent.className : '未知');
                debugLog('  - 新父容器:', colorChipsContainer.className);
            } else {
                debugLog('ℹ️ [Debug] Size Guide 按钮已在目标位置，跳过移动');
            }
            
            // 添加样式（根据设备类型应用不同样式）
            debugLog('🎨 [Debug] 应用样式到 Size Guide 按钮');
            const oldPaddingLeft = sizeGuideButton.style.paddingLeft;
            const oldMarginTop = sizeGuideButton.style.marginTop;
            
            if (isMobile()) {
                // 移动端样式
                sizeGuideButton.style.paddingLeft = '2%';
                sizeGuideButton.style.marginTop = '2%';
                debugLog('  - 原样式 paddingLeft:', oldPaddingLeft || '未设置');
                debugLog('  - 新样式 paddingLeft: 2% (移动端)');
                debugLog('  - 原样式 marginTop:', oldMarginTop || '未设置');
                debugLog('  - 新样式 marginTop: 2% (移动端)');
            } else {
                // 桌面端样式
                sizeGuideButton.style.paddingLeft = '4%';
                debugLog('  - 原样式 paddingLeft:', oldPaddingLeft || '未设置');
                debugLog('  - 新样式 paddingLeft: 4% (桌面端)');
            }
        } else {
            debugLog('ℹ️ [Debug] Size Guide 按钮未找到');
        }
        
        debugLog('✅ [Debug] Size 选项布局调整完成\n');
    }

    /**
     * 【桌面端】移动数量选择器到提交按钮组
     * 操作：将数量选择器从独立容器移动到提交按钮组的第一位
     * 源容器：.product__controls-group.product__controls-group-quanity
     * 目标容器：.product-form__controls-group.product-form__controls-group--submit
     * 移动元素：.product__item.product__quantity
     * 样式调整：
     *   - 目标容器设为 display: flex
     *   - 提交按钮容器 width: 80%, paddingLeft: 1%
     */
    function moveQuantitySelector() {
        debugLog('🔍 [Debug] 开始移动数量选择器');
        
        // 查找源容器（数量选择器的原始容器）
        const sourceSelector = '.product__controls-group.product__controls-group-quanity.product__block.product__block--medium.animation-fade-up-reveal';
        debugLog('🔍 [Debug] 查找源容器:', sourceSelector);
        const sourceContainer = document.querySelector(sourceSelector);
        debugLog('  - 找到:', sourceContainer ? '是' : '否');
        if (sourceContainer) {
            debugLog('  - 源容器类名:', sourceContainer.className);
        }
        
        // 查找目标容器（提交按钮组）
        const targetSelector = '.product-form__controls-group.product-form__controls-group--submit';
        debugLog('🔍 [Debug] 查找目标容器:', targetSelector);
        const targetContainer = document.querySelector(targetSelector);
        debugLog('  - 找到:', targetContainer ? '是' : '否');
        if (targetContainer) {
            debugLog('  - 目标容器类名:', targetContainer.className);
        }
        
        // 查找要移动的数量选择器元素
        const quantitySelector = '.product__item.product__quantity';
        debugLog('🔍 [Debug] 查找数量选择器元素:', quantitySelector);
        const quantityElement = document.querySelector(quantitySelector);
        debugLog('  - 找到:', quantityElement ? '是' : '否');
        if (quantityElement) {
            debugLog('  - 数量元素类名:', quantityElement.className);
            debugLog('  - 当前位置:', quantityElement.parentNode ? quantityElement.parentNode.className : '未知');
        }
        
        // 查找提交按钮容器
        const submitSelector = '.product-form__item.product-form__item--submit';
        debugLog('🔍 [Debug] 查找提交按钮容器:', submitSelector);
        const submitItem = document.querySelector(submitSelector);
        debugLog('  - 找到:', submitItem ? '是' : '否');
        
        // 检查所有必需元素是否存在
        if (sourceContainer && targetContainer && quantityElement) {
            // 【操作1】隐藏源容器
            debugLog('🎨 [Debug] 隐藏源容器');
            const oldSourceDisplay = sourceContainer.style.display;
            sourceContainer.style.display = 'none';
            // debugLog('✅ [桌面端] 数量选择器源容器已隐藏');
            debugLog('  - 原样式 display:', oldSourceDisplay || '未设置');
            debugLog('  - 新样式 display: none');
            
            // 【操作2】设置目标容器为 flex 布局
            debugLog('🎨 [Debug] 设置目标容器为 flex 布局');
            const oldTargetDisplay = targetContainer.style.display;
            targetContainer.style.display = 'flex';
            // debugLog('✅ [桌面端] 提交按钮组已设置为 flex 布局');
            debugLog('  - 原样式 display:', oldTargetDisplay || '未设置');
            debugLog('  - 新样式 display: flex');
            
            // 【操作3】设置提交按钮容器样式
            if (submitItem) {
                debugLog('🎨 [Debug] 设置提交按钮容器样式');
                const oldWidth = submitItem.style.width;
                const oldPaddingLeft = submitItem.style.paddingLeft;
                submitItem.style.width = '80%';
                submitItem.style.paddingLeft = '1%';
                // debugLog('✅ [桌面端] 提交按钮容器样式已应用');
                debugLog('  - 原样式 width:', oldWidth || '未设置');
                debugLog('  - 新样式 width: 80%');
                debugLog('  - 原样式 paddingLeft:', oldPaddingLeft || '未设置');
                debugLog('  - 新样式 paddingLeft: 1%');
            } else {
                debugLog('ℹ️ [Debug] 提交按钮容器未找到，跳过样式设置');
            }
            
            // 【操作4】移动数量选择器到目标容器的第一位
            const isAlreadyInTarget = targetContainer.contains(quantityElement);
            debugLog('📦 [Debug] 检查数量选择器位置');
            debugLog('  - 已在目标容器内:', isAlreadyInTarget ? '是' : '否');
            
            if (!isAlreadyInTarget) {
                debugLog('📦 [Debug] 开始移动数量选择器');
                const oldParent = quantityElement.parentNode;
                targetContainer.insertBefore(quantityElement, targetContainer.firstChild);
                // debugLog('✅ [桌面端] 数量选择器已移动到提交按钮组的第一位');
                debugLog('  - 原父容器:', oldParent ? oldParent.className : '未知');
                debugLog('  - 新父容器:', targetContainer.className);
                debugLog('  - 在目标容器中的位置: 第一位');
            } else {
                debugLog('ℹ️ [Debug] 数量选择器已在目标容器内，跳过移动');
            }
        } else {
            // console.error('❌ [桌面端] 数量选择器移动失败：缺少必需元素');
            debugError('  - 源容器未找到:', sourceSelector);
            debugError('  - 目标容器未找到:', targetSelector);
            debugError('  - 数量选择器元素未找到:', quantitySelector);
        }
        
        debugLog('✅ [Debug] 数量选择器移动操作完成\n');
    }

    /**
     * 【桌面端】移动评价徽章到产品材质容器
     * 操作：将评价徽章移动到产品材质信息容器的最后一个子元素位置
     * 源元素：.jdgm-widget.jdgm-preview-badge
     * 目标容器：.product-fabric
     * 位置：作为最后一个子元素
     */
    function moveReviewBadge() {
        debugLog('🔍 [Debug] 开始移动评价徽章');
        
        // 查找要移动的评价徽章元素
        const badgeSelector = '.jdgm-widget.jdgm-preview-badge';
        debugLog('🔍 [Debug] 查找评价徽章元素:', badgeSelector);
        const elementToMove = document.querySelector(badgeSelector);
        debugLog('  - 找到:', elementToMove ? '是' : '否');
        if (elementToMove) {
            debugLog('  - 评价徽章类名:', elementToMove.className);
            debugLog('  - 当前位置:', elementToMove.parentNode ? elementToMove.parentNode.className : '未知');
        }
        
        // 查找目标容器（产品材质信息容器）
        const fabricSelector = '.product-fabric';
        debugLog('🔍 [Debug] 查找目标容器:', fabricSelector);
        const targetContainer = document.querySelector(fabricSelector);
        debugLog('  - 找到:', targetContainer ? '是' : '否');
        if (targetContainer) {
            debugLog('  - 目标容器类名:', targetContainer.className);
            debugLog('  - 目标容器当前子元素数量:', targetContainer.children.length);
        }
        
        // 检查元素和目标容器是否存在
        if (elementToMove && targetContainer) {
            const isAlreadyInTarget = targetContainer.contains(elementToMove);
            debugLog('📦 [Debug] 检查评价徽章位置');
            debugLog('  - 已在目标容器内:', isAlreadyInTarget ? '是' : '否');
            
            if (!isAlreadyInTarget) {
                debugLog('📦 [Debug] 开始移动评价徽章');
                const oldParent = elementToMove.parentNode;
                targetContainer.appendChild(elementToMove);
                // debugLog('✅ 评价徽章已移动到产品材质容器内');
                debugLog('  - 原父容器:', oldParent ? oldParent.className : '未知');
                debugLog('  - 新父容器:', targetContainer.className);
                debugLog('  - 在目标容器中的位置: 最后一个子元素');
                debugLog('  - 目标容器新子元素数量:', targetContainer.children.length);
            } else {
                debugLog('ℹ️ [Debug] 评价徽章已在目标容器内，跳过移动');
            }
        } else {
            // console.error('❌ [桌面端] 评价徽章移动失败');
            debugError('  - 评价徽章元素未找到:', badgeSelector);
            debugError('  - 目标容器未找到:', fabricSelector);
        }
        
        debugLog('✅ [Debug] 评价徽章移动操作完成\n');
    }

    /**
     * 应用桌面端布局
     * 执行所有桌面端布局调整
     */
    function applyDesktopLayout() {
        const windowWidth = window.innerWidth;
        debugLog('🖥️ 应用桌面端布局（≥768px）');
        debugLog('  - 当前窗口宽度:', windowWidth + 'px');
        debugLog('  - 设备类型: 桌面端\n');
        adjustSizeOptionLayout();
        moveQuantitySelector();
        moveReviewBadge();
        debugLog('✅ [Debug] 桌面端布局应用完成\n');
    }

    // ============================================
    // 移动端布局逻辑（<768px）
    // ============================================

    /**
     * 【移动端】移动颜色选择器到产品图片下方
     * 操作：将颜色选择器（Colour选项）移动到产品图片容器之后
     * 源元素：[data-index="option2"]（Colour选项容器）
     * 目标位置：.product__media-container 之后，.product__details 之前
     */
    function moveColorSelectorToTop() {
        debugLog('🔍 [Debug] 开始移动颜色选择器到图片下方');
        
        // 查找颜色选择器容器（Colour选项）
        const colorSelector = '[data-index="option2"]';
        debugLog('🔍 [Debug] 查找颜色选择器容器:', colorSelector);
        const colorOptionContainer = document.querySelector(colorSelector);
        debugLog('  - 找到:', colorOptionContainer ? '是' : '否');
        if (colorOptionContainer) {
            debugLog('  - 颜色选择器 data-index:', colorOptionContainer.getAttribute('data-index'));
            debugLog('  - 当前位置:', colorOptionContainer.parentNode ? colorOptionContainer.parentNode.className : '未知');
        }
        
        // 查找产品图片容器
        const mediaSelector = '.product__media-container';
        debugLog('🔍 [Debug] 查找产品图片容器:', mediaSelector);
        const mediaContainer = document.querySelector(mediaSelector);
        debugLog('  - 找到:', mediaContainer ? '是' : '否');
        if (mediaContainer) {
            debugLog('  - 图片容器类名:', mediaContainer.className);
        }
        
        // 查找产品详情容器
        const detailsSelector = '.product__details';
        debugLog('🔍 [Debug] 查找产品详情容器:', detailsSelector);
        const detailsContainer = document.querySelector(detailsSelector);
        debugLog('  - 找到:', detailsContainer ? '是' : '否');
        if (detailsContainer) {
            debugLog('  - 详情容器类名:', detailsContainer.className);
        }
        
        if (colorOptionContainer && mediaContainer && detailsContainer) {
            // 检查颜色选择器是否已经在正确位置（在图片之后，详情之前）
            const mediaNextSibling = mediaContainer.nextElementSibling;
            const isInCorrectPosition = mediaNextSibling === colorOptionContainer;
            
            debugLog('📦 [Debug] 检查颜色选择器位置');
            debugLog('  - 图片容器的下一个兄弟元素:', mediaNextSibling ? mediaNextSibling.className || mediaNextSibling.getAttribute('data-index') : '无');
            debugLog('  - 颜色选择器:', colorOptionContainer.getAttribute('data-index'));
            debugLog('  - 已在正确位置:', isInCorrectPosition ? '是' : '否');
            
            if (!isInCorrectPosition) {
                debugLog('📦 [Debug] 开始移动颜色选择器');
                const oldParent = colorOptionContainer.parentNode;
                const oldNextSibling = colorOptionContainer.nextElementSibling;
                mediaContainer.parentNode.insertBefore(colorOptionContainer, detailsContainer);
                // debugLog('✅ [移动端] 颜色选择器已移动到产品图片下方');
                debugLog('  - 原父容器:', oldParent ? oldParent.className : '未知');
                debugLog('  - 新父容器:', mediaContainer.parentNode ? mediaContainer.parentNode.className : '未知');
                debugLog('  - 原下一个兄弟元素:', oldNextSibling ? oldNextSibling.className : '无');
                debugLog('  - 新下一个兄弟元素:', colorOptionContainer.nextElementSibling ? colorOptionContainer.nextElementSibling.className : '无');
                debugLog('  - 目标位置: 图片容器之后，详情容器之前');
            } else {
                debugLog('ℹ️ [Debug] 颜色选择器已在正确位置，跳过移动');
            }
        } else {
            // console.error('❌ [移动端] 颜色选择器移动失败：缺少必需元素');
            debugError('  - 颜色选择器未找到:', colorSelector);
            debugError('  - 图片容器未找到:', mediaSelector);
            debugError('  - 详情容器未找到:', detailsSelector);
        }
        
        debugLog('✅ [Debug] 颜色选择器移动操作完成\n');
    }

    /**
     * 【移动端】移动尺寸选择器到材质说明之后
     * 操作：将尺寸选择器（Size选项）移动到产品材质说明容器之后
     * 源元素：[data-index="option1"]（Size选项容器）
     * 目标位置：.product-fabric 之后，.product__price 之前
     */
    function moveSizeSelectorAfterFabric() {
        debugLog('🔍 [Debug] 开始移动尺寸选择器到材质说明之后');
        
        // 查找尺寸选择器容器（Size选项）
        const sizeSelector = '[data-index="option1"]';
        debugLog('🔍 [Debug] 查找尺寸选择器容器:', sizeSelector);
        const sizeOptionContainer = document.querySelector(sizeSelector);
        debugLog('  - 找到:', sizeOptionContainer ? '是' : '否');
        if (sizeOptionContainer) {
            debugLog('  - 尺寸选择器 data-index:', sizeOptionContainer.getAttribute('data-index'));
            debugLog('  - 当前位置:', sizeOptionContainer.parentNode ? sizeOptionContainer.parentNode.className : '未知');
        }
        
        // 查找产品材质容器
        const fabricSelector = '.product-fabric';
        debugLog('🔍 [Debug] 查找产品材质容器:', fabricSelector);
        const fabricContainer = document.querySelector(fabricSelector);
        debugLog('  - 找到:', fabricContainer ? '是' : '否');
        if (fabricContainer) {
            debugLog('  - 材质容器类名:', fabricContainer.className);
        }
        
        // 查找价格容器
        const priceSelector = '.product__price';
        debugLog('🔍 [Debug] 查找价格容器:', priceSelector);
        const priceContainer = document.querySelector(priceSelector);
        debugLog('  - 找到:', priceContainer ? '是' : '否');
        if (priceContainer) {
            debugLog('  - 价格容器类名:', priceContainer.className);
        }
        
        if (sizeOptionContainer && fabricContainer && priceContainer) {
            // 检查尺寸选择器是否已经在正确位置
            const fabricNextSibling = fabricContainer.nextElementSibling;
            const isInCorrectPosition = fabricNextSibling === sizeOptionContainer;
            
            debugLog('📦 [Debug] 检查尺寸选择器位置');
            debugLog('  - 材质容器的下一个兄弟元素:', fabricNextSibling ? fabricNextSibling.className || fabricNextSibling.getAttribute('data-index') : '无');
            debugLog('  - 尺寸选择器:', sizeOptionContainer.getAttribute('data-index'));
            debugLog('  - 已在正确位置:', isInCorrectPosition ? '是' : '否');
            
            if (!isInCorrectPosition) {
                debugLog('📦 [Debug] 开始移动尺寸选择器');
                const oldParent = sizeOptionContainer.parentNode;
                const oldNextSibling = sizeOptionContainer.nextElementSibling;
                fabricContainer.parentNode.insertBefore(sizeOptionContainer, priceContainer);
                // debugLog('✅ [移动端] 尺寸选择器已移动到材质说明之后');
                debugLog('  - 原父容器:', oldParent ? oldParent.className : '未知');
                debugLog('  - 新父容器:', fabricContainer.parentNode ? fabricContainer.parentNode.className : '未知');
                debugLog('  - 原下一个兄弟元素:', oldNextSibling ? oldNextSibling.className : '无');
                debugLog('  - 新下一个兄弟元素:', sizeOptionContainer.nextElementSibling ? sizeOptionContainer.nextElementSibling.className : '无');
                debugLog('  - 目标位置: 材质容器之后，价格容器之前');
            } else {
                debugLog('ℹ️ [Debug] 尺寸选择器已在正确位置，跳过移动');
            }
        } else {
            // console.error('❌ [移动端] 尺寸选择器移动失败：缺少必需元素');
            debugError('  - 尺寸选择器未找到:', sizeSelector);
            debugError('  - 材质容器未找到:', fabricSelector);
            debugError('  - 价格容器未找到:', priceSelector);
        }
        
        debugLog('✅ [Debug] 尺寸选择器移动操作完成\n');
    }

    /**
     * 应用移动端布局
     * 执行所有移动端布局调整
     * 
     * 注意：
     * 1. Complete the look 模块和加购按钮本来就在页面最后，不需要移动
     * 2. Size 选项的样式调整（隐藏标签、移动 Size Guide 按钮）在桌面端和移动端都需要应用
     * 3. 评价徽章移动到 fabric 容器在桌面端和移动端都需要应用
     */
    function applyMobileLayout() {
        const windowWidth = window.innerWidth;
        debugLog('📱 应用移动端布局（<768px）');
        debugLog('  - 当前窗口宽度:', windowWidth + 'px');
        debugLog('  - 设备类型: 移动端\n');
        
        // 【移动端特有】移动颜色选择器到图片下方
        moveColorSelectorToTop();
        
        // 【移动端特有】移动尺寸选择器到材质说明之后
        moveSizeSelectorAfterFabric();
        
        // 【桌面端和移动端共用】调整 Size 选项布局（隐藏 Size 标签，移动 Size Guide 按钮并添加样式）
        adjustSizeOptionLayout();
        
        // 【桌面端和移动端共用】移动评价徽章到产品材质容器内
        moveReviewBadge();
        
        // 【移动端特有】添加价格容器样式
        applyMobilePriceStyles();
        
        // Complete the look 模块和加购按钮已经在最后，无需移动
        debugLog('✅ [Debug] 移动端布局应用完成\n');
    }

    /**
     * 【移动端】添加价格容器样式
     * 操作：为价格容器添加 margin-top: 5%
     * 目标元素：.product__price.product__block.animation-fade-up-reveal.product__block--small
     * 样式：margin-top: 5%
     */
    function applyMobilePriceStyles() {
        debugLog('🎨 [Debug] 开始应用移动端价格容器样式');
        
        // 查找价格容器
        const priceSelector = '.product__price.product__block.animation-fade-up-reveal.product__block--small';
        debugLog('🔍 [Debug] 查找价格容器:', priceSelector);
        const priceContainer = document.querySelector(priceSelector);
        debugLog('  - 找到:', priceContainer ? '是' : '否');
        
        if (priceContainer) {
            const oldMarginTop = priceContainer.style.marginTop;
            priceContainer.style.marginTop = '5%';
            // debugLog('✅ [移动端] 价格容器已添加 margin-top: 5%');
            debugLog('  - 原样式 marginTop:', oldMarginTop || '未设置');
            debugLog('  - 新样式 marginTop: 5%');
        } else {
            // 尝试更简单的选择器
            const simplePriceSelector = '.product__price';
            debugLog('🔍 [Debug] 尝试简单选择器:', simplePriceSelector);
            const simplePriceContainer = document.querySelector(simplePriceSelector);
            debugLog('  - 找到:', simplePriceContainer ? '是' : '否');
            
            if (simplePriceContainer) {
                const oldMarginTop = simplePriceContainer.style.marginTop;
                simplePriceContainer.style.marginTop = '5%';
                // debugLog('✅ [移动端] 价格容器已添加 margin-top: 5%');
                debugLog('  - 原样式 marginTop:', oldMarginTop || '未设置');
                debugLog('  - 新样式 marginTop: 5%');
            } else {
                // console.error('❌ [移动端] 价格容器未找到');
                debugError('  - 价格容器未找到:', priceSelector);
            }
        }
        
        debugLog('✅ [Debug] 移动端价格容器样式应用完成\n');
    }

    // ============================================
    // 主初始化函数
    // ============================================

    /**
     * 初始化布局控制
     * 根据当前设备类型应用相应的布局
     */
    function initLayoutControl() {
        // 防止重复执行
        if (isLayoutApplied) {
            debugLog('ℹ️ [Debug] 布局已应用，跳过重复执行');
            return;
        }

        debugLog('═══════════════════════════════════════');
        debugLog('🚀 [Debug] 开始初始化布局控制');
        debugLog('═══════════════════════════════════════');
        
        // 检查是否为产品详情页
        const isProduct = isProductPage();
        debugLog('🔍 [Debug] 检查页面类型');
        debugLog('  - 是否为产品详情页:', isProduct ? '是' : '否');
        
        if (!isProduct) {
            debugLog('ℹ️ PDP Layout Control: Skipped (not a product page)');
            debugLog('═══════════════════════════════════════\n');
            return;
        }

        // 根据设备类型应用布局
        const mobile = isMobile();
        debugLog('🔍 [Debug] 检测设备类型');
        debugLog('  - 窗口宽度:', window.innerWidth + 'px');
        debugLog('  - 移动端断点:', MOBILE_BREAKPOINT + 'px');
        debugLog('  - 是否为移动端:', mobile ? '是' : '否');
        debugLog('');

        if (mobile) {
            applyMobileLayout();
        } else {
            applyDesktopLayout();
        }
        
        // 标记布局已应用
        isLayoutApplied = true;
        
        debugLog('═══════════════════════════════════════');
        debugLog('✅ [Debug] 布局控制初始化完成');
        debugLog('═══════════════════════════════════════\n');
    }

    /**
     * 使用 MutationObserver 处理动态内容
     * 监听 DOM 变化，当目标元素出现时执行布局调整
     */
    function setupLayoutObserver() {
        if (!isProductPage()) {
            return;
        }
        
        let observerTimeout;
        const observer = new MutationObserver(function(mutations) {
            // 防抖处理，避免频繁触发
            clearTimeout(observerTimeout);
            observerTimeout = setTimeout(function() {
                mutations.forEach(function(mutation) {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        // 检查目标元素是否存在
                        const hasRequiredElements = 
                            document.querySelector('[data-index^="option"]') &&
                            document.querySelector('.product__media-container') &&
                            document.querySelector('.product__details');
                        
                        if (hasRequiredElements && !isLayoutApplied) {
                            debugLog('🔄 [Debug] 检测到 DOM 变化，重新应用布局');
                            // 重置标志，允许重新应用布局
                            isLayoutApplied = false;
                            // 重新应用布局
                            initLayoutControl();
                            // 断开观察器，避免重复执行
                            observer.disconnect();
                        }
                    }
                });
            }, 500); // 500ms 防抖
        });

        // 开始观察
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    // ============================================
    // 事件监听
    // ============================================

    /**
     * DOM 加载完成后初始化
     */
    function onDOMReady() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', function() {
                initLayoutControl();
                setupLayoutObserver();
            });
        } else {
            initLayoutControl();
            setupLayoutObserver();
        }
    }

    /**
     * 窗口大小改变时重新应用布局
     */
    function onWindowResize() {
        const debouncedResize = debounce(function() {
            if (isProductPage()) {
                // 重置标志，允许重新应用布局（窗口大小改变时）
                isLayoutApplied = false;
                initLayoutControl();
            }
        }, 250);
        
        window.addEventListener('resize', debouncedResize);
        window.addEventListener('orientationchange', debouncedResize);
    }

    // ============================================
    // 启动
    // ============================================

    // 初始化
    onDOMReady();
    onWindowResize();

})();
